import { useMergeRefs } from '@floating-ui/react';
import { compact, isUndefined } from 'lodash';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import MentionsProvider from '../../services/mentions-provider';
import { ReactRendererProps } from '../../types/components';
import { Dropdown, DropdownRef, Menu, MenuItem, Search } from '../Dropdown';
import Icon, { IconName } from '../Icon';

export type LeafMentionSuggestion = {
  slug: string;
  label: string;
  icon?: IconName;
  type?: string;
  available: boolean;
  suggestions?: never;
  loaded?: boolean;
  config?: {
    type?: {
      label: string;
    };
  };
};
export type ParentMentionSuggestion = {
  // Server-paged groups (e.g. notes) carry a slug; flow groups are matched by label.
  slug?: string;
  suggestions: MentionSuggestion[];
  label: string;
  icon?: IconName;
  available: boolean;
  loaded?: boolean;
  has_more?: boolean | undefined;
  next_offset?: number | undefined;
};

export type MentionSuggestion = LeafMentionSuggestion | ParentMentionSuggestion;

export type MentionsListProps = ReactRendererProps<null> & {
  referenceElement: HTMLElement | null;
  query?: string | undefined;
  mentionsProvider: MentionsProvider;
  onSelect: (suggestion: LeafMentionSuggestion) => void;
  onOpen?: (() => void | undefined) | undefined;
  onClose?: (() => void | undefined) | undefined;
  handleKeyboard?: boolean | undefined;
  onDropdownOpen?: (key: string) => void;
  onLoadMore?: (key: string, offset: number) => void;
  onSearch?: (query: string, onReply: (suggestions: MentionSuggestion[]) => void) => void;
  serverSearch?: boolean;
  portalRoot?: HTMLElement | undefined;
};

export const NoMentionsFoundComponent = () => {
  return <div className='mention-menu__empty'>No mentions found</div>;
};

export const NoMentionsAvailableComponent = () => {
  return <div className='mention-menu__empty'>No mentions available</div>;
};

/**
 * Loading component displayed when waiting for backend response for new mentions.
 * This component is shown when a suggestion has `loaded: false` and is waiting
 * for the backend to return new suggestions for that category.
 */
export const MentionsLoadingComponent = () => {
  return <div className='mention-menu__loading' />;
};

const LoadMoreButton = ({
  loading,
  disableHover,
  onLoad,
}: {
  loading: boolean;
  disableHover: boolean;
  onLoad: () => void;
}) => {
  const label = loading ? 'Loading…' : 'Load more';

  return (
    <MenuItem
      label={label}
      className='mention-menu__load-more'
      keepOpenOnClick
      disabled={loading}
      disableHover={disableHover}
      // The menu stays open, so the editor must keep focus or TipTap stops
      // forwarding arrow/Enter keys to the dropdown.
      onMouseDown={event => event.preventDefault()}
      onClick={onLoad}
    >
      <div className='mention-menu__load-more-label'>{label}</div>
    </MenuItem>
  );
};

const humanReadableType = (suggestion: LeafMentionSuggestion) => {
  return suggestion.config?.type?.label || suggestion.type;
};

const SEARCH_DEBOUNCE_MS = 150;

export const MentionsList = forwardRef<DropdownRef, MentionsListProps>(
  (
    {
      referenceElement,
      mentionsProvider,
      handleKeyboard,
      onSelect,
      onDropdownOpen,
      onLoadMore,
      onSearch,
      serverSearch = false,
      portalRoot,
      ...props
    },
    ref,
  ) => {
    if (!referenceElement) return null;

    const [onOpen, setOnOpen] = useState<(() => void | undefined) | undefined>(props.onOpen);
    const [onClose, setOnClose] = useState<(() => void | undefined) | undefined>(props.onClose);
    const dropdownRef = useRef<DropdownRef>(null);
    const lastInteractionRef = useRef<'mouse' | 'keyboard'>('mouse');
    const keyboardLockRef = useRef(0);
    const [interactionMode, setInteractionMode] = useState<'mouse' | 'keyboard'>('mouse');
    const [updateTrigger, setUpdateTrigger] = useState(0);

    // Listen for updates from MentionsProvider and force re-renders when data changes
    useEffect(() => {
      const unsubscribe = mentionsProvider.onUpdate(() => {
        setUpdateTrigger(prev => prev + 1);
      });

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }, [mentionsProvider]);

    const setMode = (mode: 'mouse' | 'keyboard') => {
      if (lastInteractionRef.current === mode) return;
      lastInteractionRef.current = mode;
      setInteractionMode(mode);
    };

    const keyboardCallback = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
        setMode('keyboard');
        keyboardLockRef.current = Date.now() + 500;
        dropdownRef.current?.onKeyDown(e);
        e.preventDefault();
      }
    };

    useEffect(() => {
      setOnOpen(() => {
        if (props.onOpen) props.onOpen();

        if (onDropdownOpen) {
          // console.log('Main mention dropdown opened');
          // onDropdownOpen('root');
        }

        if (handleKeyboard) {
          document.addEventListener('keydown', keyboardCallback);
        }
      });

      return () => {
        if (handleKeyboard) {
          document.removeEventListener('keydown', keyboardCallback);
        }
      };
    }, [props.onOpen, onDropdownOpen]);

    useEffect(() => {
      setOnClose(() => {
        if (props.onClose) props.onClose();

        if (handleKeyboard) {
          document.removeEventListener('keydown', keyboardCallback);
        }
      });
    }, [props.onClose]);

    const showSearch = isUndefined(props.query);
    const [localQuery, setLocalQuery] = useState('');
    const query = showSearch ? localQuery : (props.query ?? '');

    const searchSeq = useRef(0);
    const [searchLoading, setSearchLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({});

    // Any provider update means the page we waited for landed.
    useEffect(() => {
      setLoadingMore({});
    }, [updateTrigger]);

    useEffect(() => {
      if (!serverSearch || !onSearch) return;

      const nextQuery = query.trim();
      if (!nextQuery) {
        mentionsProvider.clearSearch();
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      const seq = ++searchSeq.current;
      const timeoutId = window.setTimeout(() => {
        onSearch(nextQuery, suggestions => {
          if (seq !== searchSeq.current) return;
          mentionsProvider.replaceSearchSuggestions(nextQuery, suggestions);
          setSearchLoading(false);
        });
      }, SEARCH_DEBOUNCE_MS);

      return () => window.clearTimeout(timeoutId);
    }, [query, serverSearch, onSearch, mentionsProvider]);

    // Server hits replace the local ones only once they arrive, so the menu never
    // blanks out between keystrokes.
    const serverHitsReady = serverSearch && mentionsProvider.remoteSearch?.query === query.trim();

    // Simple memoization to trigger re-renders when suggestions update
    const suggestions = useMemo(() => mentionsProvider.availableSuggestions, [updateTrigger]);
    const searchResults = useMemo(() => {
      if (query && query.length > 0) {
        return serverHitsReady
          ? mentionsProvider.flattenLeaves(mentionsProvider.remoteSearch?.suggestions ?? [])
          : mentionsProvider.searchMentions(query, { availableOnly: true });
      }
      return null;
    }, [query, updateTrigger, serverHitsReady]);

    if (query && query.length > 0) {
      const results = searchResults || [];
      const awaitingServer = serverSearch && searchLoading && !serverHitsReady;

      // Show "No mentions found" when search returns no results
      if (results.length === 0) {
        return (
          <Dropdown
            key='search'
            ref={useMergeRefs([dropdownRef, ref])}
            referenceElement={referenceElement}
            portalRoot={portalRoot}
            referenceOffset={18}
            onOpen={onOpen}
            onClose={onClose}
            dropdownClassNames='mention-menu'
          >
            {showSearch && (
              <Search
                className='mention-menu__search'
                placeholder='Search…'
                onChange={e => setLocalQuery(e.target.value)}
                value={query ?? ''}
                onKeyDown={e => {
                  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
                    dropdownRef.current?.onKeyDown(e.nativeEvent);
                    e.preventDefault();
                  }
                }}
              />
            )}
            {awaitingServer ? <MentionsLoadingComponent /> : <NoMentionsFoundComponent />}
          </Dropdown>
        );
      }

      const renderParentsTitle = (parents: MentionSuggestion[]) =>
        parents
          .map(parent => parent.label)
          .reduce<string[]>((acc, el) => compact([...acc, acc.length > 0 ? '·' : undefined, el]), [])
          .join(' ');

      const renderParents = (parents: MentionSuggestion[]) =>
        parents
          .map((parent: MentionSuggestion, index: number) => (
            <div key={parent.slug || `${parent.label}-${index}`} className='text-base-500 flex items-center gap-x-1'>
              <div className='mention-menu__label mention-menu__label--secondary'>{parent.label}</div>
            </div>
          ))
          .reduce<JSX.Element[]>(
            (acc, el, index) =>
              compact([...acc, acc.length > 0 ? <div key={`separator-${index}`}>&middot;</div> : undefined, el]),
            [],
          );

      return (
        <Dropdown
          key='search'
          ref={useMergeRefs([dropdownRef, ref])}
          referenceElement={referenceElement}
          portalRoot={portalRoot}
          referenceOffset={18}
          onOpen={onOpen}
          onClose={onClose}
          dropdownClassNames='mention-menu'
        >
          {showSearch && (
            <Search
              className='mention-menu__search'
              placeholder='Search…'
              onChange={e => setLocalQuery(e.target.value)}
              value={query ?? ''}
              onKeyDown={e => {
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
                  dropdownRef.current?.onKeyDown(e.nativeEvent);
                  e.preventDefault();
                }
              }}
            />
          )}
          {results.map(({ suggestion, parents }, index) => (
            <MenuItem
              key={`${parents.map(parent => parent.slug || parent.label).join('/')}/${suggestion.slug || suggestion.label}-${index}`}
              label={suggestion.label}
              onClick={() => onSelect(suggestion)}
              className='mention-menu__item'
            >
              <div className='flex w-full flex-col'>
                <div className='flex w-full items-center gap-x-2 text-left'>
                  {suggestion.icon && (
                    <Icon name={suggestion.icon} className='text-base-400 ml-1 h-4 w-4 flex-shrink-0' />
                  )}
                  <div className='mention-menu__label'>{suggestion.label}</div>
                  <div className='text-base-500 ml-4 flex-shrink-0 grow text-right capitalize'>
                    {humanReadableType(suggestion)}
                  </div>
                </div>

                <div className='text-base-500 ml-7 mt-1 flex items-center overflow-hidden text-xs'>
                  <Icon name='hero-arrow-turn-down-right flex-shrink-0' className='text-base-300 h-3 w-3' />
                  <div className='ml-1 flex w-full items-center gap-x-1' title={renderParentsTitle(parents)}>
                    {renderParents(parents)}
                  </div>
                </div>
              </div>
            </MenuItem>
          ))}
        </Dropdown>
      );
    } else {
      const renderMenuItems = (suggestions: MentionSuggestion[], parentId: string) => {
        if (suggestions.length === 0) {
          return <NoMentionsAvailableComponent />;
        }

        return suggestions.map((suggestion, index) => {
          const key = suggestion.slug ? suggestion.slug : `${parentId}-${index}`;
          const disableHover = interactionMode === 'keyboard';

          const safeLabel = suggestion.label ?? '';

          return suggestion.suggestions ? (
            <Menu
              key={key}
              label={safeLabel}
              icon={suggestion.icon}
              buttonClassNames='mention-menu__item'
              dropdownClassNames='mention-menu mention-menu--nested'
              closeOnParentScroll={true}
              openOnHover={!disableHover}
              disableHover={disableHover}
              onOpen={() => {
                if (suggestion.loaded == false) {
                  if (onDropdownOpen) onDropdownOpen(key);
                }
              }}
            >
              {suggestion.loaded === false ? (
                <MentionsLoadingComponent />
              ) : (
                <>
                  {renderMenuItems(suggestion.suggestions, key)}
                  {suggestion.has_more && (
                    <LoadMoreButton
                      loading={Boolean(loadingMore[key])}
                      disableHover={disableHover}
                      onLoad={() => {
                        setLoadingMore(current => ({ ...current, [key]: true }));
                        onLoadMore?.(key, suggestion.next_offset ?? 0);
                      }}
                    />
                  )}
                </>
              )}
            </Menu>
          ) : (
            <MenuItem
              key={key}
              label={safeLabel}
              className='mention-menu__item'
              disableHover={disableHover}
              onClick={() => onSelect(suggestion)}
            >
              {suggestion.icon && <Icon name={suggestion.icon} className='text-base-400 ml-1 h-5 w-5 flex-shrink-0' />}
              <div className='mention-menu__label'>{suggestion.label}</div>
              <div className='mention-menu__desc'>{humanReadableType(suggestion)}</div>
            </MenuItem>
          );
        });
      };

      return (
        <Dropdown
          key='browse'
          ref={useMergeRefs([dropdownRef, ref])}
          referenceElement={referenceElement}
          portalRoot={portalRoot}
          referenceOffset={18}
          onOpen={onOpen}
          onClose={onClose}
          dropdownClassNames='mention-menu'
        >
          <div
            onMouseMove={() => {
              if (Date.now() < keyboardLockRef.current) return;
              setMode('mouse');
            }}
          >
            {showSearch && (
              <Search
                className='mention-menu__search'
                placeholder='Search…'
                onChange={e => setLocalQuery(e.target.value)}
                value={query ?? ''}
                onKeyDown={e => {
                  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
                    dropdownRef.current?.onKeyDown(e.nativeEvent);
                    e.preventDefault();
                  }
                }}
              />
            )}
            {renderMenuItems(suggestions, 'root')}
          </div>
        </Dropdown>
      );
    }
  },
);

MentionsList.displayName = 'MentionsList';
